import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import isEmpty from 'lodash/isEmpty';

import { AiAuthorizationErrorsList } from '@lumapps/ai/components/AiAuthorizationErrorsList';
import { AskAiFollowUpQuestions } from '@lumapps/ask-ai-conversation/components/AskAiFollowUpQuestions';
import { AskAiToggleButton } from '@lumapps/ask-ai-conversation/components/AskAiToggleButton';
import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { useClassnames } from '@lumapps/classnames';
import { get as getConstants } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { SeeMoreFade } from '@lumapps/lumx-layouts/components/SeeMoreFade';
import { ErrorBoundary } from '@lumapps/lumx-suspense/components/ErrorBoundary';
import { Alignment } from '@lumapps/lumx/react';

import { trackAskExpand } from '../../api/analytics';
import { ASK_AI_MAX_HEIGHT, ASK_AI_MAX_HEIGHT_PREVIEW, ASK_AI_MAX_HEIGHT_WEBVIEW } from '../../constants';
import type { AskAiApiError, AskAiFeatures, AskSearchBestAnswer } from '../../types';
import { askAnswerHasAuthError } from '../../utils';
import { AskAiDisclaimer } from '../AskAiDisclaimer';
import { AskAiEmptyState } from '../AskAiEmptyState';
import { AskAiErrorState } from '../AskAiErrorState';
import { AskAiResultContent } from '../AskAiResultContent';

import './index.scss';

const CLASSNAME = 'ask-ai';
/** Delay (in ms) the answer must stay expanded before the ASK_EXPAND event is tracked */
export const EXPAND_TRACK_DELAY = 1000;

export interface AskAiProps {
    /** The query from which the answer has been generated. */
    query: string;
    /** The answer generated to display. */
    bestAnswer?: AskSearchBestAnswer;
    /** Classname to set on the wrapper container */
    className?: string;
    /** Error received on Ask AI query. */
    error?: AskAiApiError | null;
    /** Unique ID to identify each search */
    traceId: string;
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** Method used to remake an Ask AI query */
    refetch?: () => void;
    /** All features state. Activated or not */
    features?: AskAiFeatures;
    /** Whether the Ask Answer is currently streaming */
    isStreaming?: boolean;
    /** Handle change agentId */
    handleAgentChange?: (rule: AskAiRule) => void;
    /** Selected agent id - selected by the user */
    selectedAgent?: AskAiRule;
    /** Whether the Ask Answer is currently loading */
    isLoading?: boolean;
}

/**
 * Display the result given by the `useAskAi` hook.
 */
export const AskAi: React.FC<AskAiProps> = ({
    className,
    query,
    bestAnswer,
    error,
    traceId,
    isPreview = false,
    refetch,
    features = {
        isAgentEnabled: false,
        isConversationalEnabled: false,
    },
    isStreaming,
    handleAgentChange,
    selectedAgent,
    isLoading,
}) => {
    const { block, element } = useClassnames(CLASSNAME);

    const { applicationId } = getConstants();
    const isWebview = applicationId === AppId.webview;

    /**
     * Create a combined seed that changes when either concatenatedChunks, isStreaming or isActionableItemLoaded changes
     * Change it in a `useLayoutEffect` to calculate the seed before updating the DOM and avoid flickers
     */
    const recalculationSeed = useRef<string>('');
    useLayoutEffect(() => {
        recalculationSeed.current = `${bestAnswer?.answer}-${isStreaming}`;
    }, [bestAnswer?.answer, isStreaming]);

    /** Keeps track of the traceId for which the ASK_EXPAND tracking event has already been sent, to only send it once per answer */
    const trackedExpandTraceIdRef = useRef<string | null>(null);
    /** Pending timer confirming the expand once it has been sustained for a short while */
    const expandTrackTimerRef = useRef<ReturnType<typeof setTimeout>>();

    /** A new answer or an unmount invalidates any pending expand confirmation */
    useEffect(() => () => clearTimeout(expandTrackTimerRef.current), [traceId]);

    const noResults = isEmpty(bestAnswer?.answer) && !isStreaming && !isLoading;

    const hasAuthErrors = bestAnswer && askAnswerHasAuthError(bestAnswer);

    const isError = !!error && !isEmpty(error);

    // Show follow-up questions input only after the streaming is done
    const shouldShowFollowUpQuestions = features.isConversationalEnabled && bestAnswer?.answer && !isStreaming;

    const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);

    /**
     * Sets default SeeMoreFade max height depending on context
     * - If we're in micro-app builder: `ASK_AI_MAX_HEIGHT_PREVIEW`
     * - Else: default `ASK_AI_MAX_HEIGHT`
     */
    const seeMoreMaxHeight = useMemo(() => {
        if (isPreview) {
            return ASK_AI_MAX_HEIGHT_PREVIEW;
        }

        if (isWebview) {
            return ASK_AI_MAX_HEIGHT_WEBVIEW;
        }
        return ASK_AI_MAX_HEIGHT;
    }, [isPreview, isWebview]);

    if (isError) {
        return <AskAiErrorState error={error.response?.data?.errors?.[0]} />;
    }

    if (!isError && noResults) {
        return <AskAiEmptyState />;
    }

    const isLoadingAfterAgentSelection = isLoading && selectedAgent;

    /** Handle the agent change */
    const handleSelectedAgentChange = (rule: AskAiRule) => {
        /** Should close follow-up questions if the agent is changed */
        setIsFollowUpOpen(false);
        /** Set the selected agent value */
        handleAgentChange?.(rule);
    };

    /**
     * Send an expand event once the answer has been expanded for a sustained period of time.
     * Only sent once per traceId, to avoid sending duplicate events when the answer is expanded/collapsed several times.
     */
    const handleToggleExpand = (isOpen: boolean) => {
        // Restart the confirmation window on every change: only a sustained expand is tracked
        clearTimeout(expandTrackTimerRef.current);

        // No tracking without a traceId (e.g. embedded web component): the event could not be joined to anything
        if (!isOpen || !traceId || isPreview || trackedExpandTraceIdRef.current === traceId) {
            return;
        }

        expandTrackTimerRef.current = setTimeout(() => {
            trackedExpandTraceIdRef.current = traceId;
            trackAskExpand(traceId, query);
        }, EXPAND_TRACK_DELAY);
    };

    return (
        <article
            className={block(
                {
                    'vertical-center': Boolean(isError || noResults),
                    'has-follow-up': Boolean(isFollowUpOpen),
                },
                className,
            )}
        >
            <SeeMoreFade
                maxHeight={seeMoreMaxHeight}
                recalculationSeed={recalculationSeed.current}
                vAlign={Alignment.center}
                hAlign={Alignment.top}
                orientation="vertical"
                renderToggleButton={(toggleDisplay, isMoreOpen) =>
                    !isFollowUpOpen &&
                    !(isLoadingAfterAgentSelection || isStreaming) && (
                        <AskAiToggleButton
                            onClick={toggleDisplay}
                            isMoreOpen={isMoreOpen}
                            isConversationalEnabled={features.isConversationalEnabled}
                        />
                    )
                }
                previewOnHover={features.isConversationalEnabled}
                onOpenChange={handleToggleExpand}
                onPreviewChange={handleToggleExpand}
            >
                <div className={element('result')}>
                    <div className={element('result-details')}>
                        <AskAiDisclaimer isHumanCurated={bestAnswer?.isHumanCurated} isWebview={isWebview} />
                        <AskAiResultContent
                            className={element('value')}
                            answer={bestAnswer || {}}
                            originalQuery={query}
                            traceId={traceId}
                            isPreview={isPreview}
                            isStreaming={isStreaming}
                            isLoading={(isStreaming || isLoadingAfterAgentSelection) && !bestAnswer?.answer}
                            shouldDisplayAgentName={!isPreview && features.isAgentEnabled}
                            handleAgentChange={handleSelectedAgentChange}
                        />

                        {hasAuthErrors && (
                            <AiAuthorizationErrorsList authErrors={bestAnswer.authErrors} refetch={refetch} />
                        )}

                        {shouldShowFollowUpQuestions && !isWebview && (
                            <ErrorBoundary>
                                <AskAiFollowUpQuestions
                                    isOpen={isFollowUpOpen}
                                    setIsOpen={setIsFollowUpOpen}
                                    initialTraceId={traceId}
                                    initialSuggestedQuestions={bestAnswer?.suggestedFollowUpQuestions}
                                    conversationId={bestAnswer?.conversationId}
                                    isInitialResultStreaming={isStreaming}
                                    initialSelectedAgent={selectedAgent}
                                />
                            </ErrorBoundary>
                        )}
                    </div>
                </div>
            </SeeMoreFade>
        </article>
    );
};
